import { Search, SlidersHorizontal } from "lucide-react";
import { AppCard } from "@/components/app-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppData } from "@/data/data-context";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/store/ui";
import { useMobileInterface } from "@/lib/mobile";

export function CatalogPage() {
  const { apps } = useAppData();
  const mobile = useMobileInterface();
  const { catalogQuery, activeCategory, setCatalogQuery, setActiveCategory, showDesktopApps, setShowDesktopApps } = useUiStore();
  const approved = apps.filter((app) => app.status === "approved");
  const compatible = approved.filter((app) => !mobile || showDesktopApps || app.mobileSupported === true);
  const categories = ["All", ...Array.from(new Set(compatible.flatMap((app) => app.categories)))];
  const selectedCategory = categories.includes(activeCategory) ? activeCategory : "All";
  const query = catalogQuery.trim().toLowerCase();
  const visible = compatible.filter((app) => {
    const categoryMatch = selectedCategory === "All" || app.categories.includes(selectedCategory);
    const searchMatch = !query || [app.name, app.summary, ...app.tags, ...app.categories].join(" ").toLowerCase().includes(query);
    return categoryMatch && searchMatch;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-7 sm:py-10">
      <section className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
        <h1 className="font-display text-3xl tracking-tight">Apps</h1>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={catalogQuery} onChange={(event) => setCatalogQuery(event.target.value)} placeholder="Search apps…" className="pl-10" aria-label="Search app catalog" />
        </div>
      </section>

      {mobile && <label className="mt-5 flex min-h-11 items-center gap-3 text-sm font-bold"><input type="checkbox" checked={showDesktopApps} onChange={(event) => setShowDesktopApps(event.target.checked)} />Show desktop apps</label>}
      {categories.length > 1 && <div className="mt-6 flex flex-wrap items-center gap-2 pb-2" aria-label="Filter by category">
        <SlidersHorizontal className="mr-1 size-4 shrink-0 text-muted-foreground" />
        {categories.map((category) => <Button key={category} variant={category === selectedCategory ? "default" : "outline"} size="sm" className={cn("shrink-0", category === selectedCategory && "shadow-sm")} onClick={() => setActiveCategory(category)}>{category}</Button>)}
      </div>}

      <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((app, index) => <AppCard key={app.id} app={app} index={index} />)}
      </div>
      {visible.length === 0 && <div className="mt-10 rounded-2xl border border-dashed border-border p-6 text-center">
        <p className="font-display text-lg">{mobile && !showDesktopApps && compatible.length === 0 ? "No mobile apps available yet" : "No apps found"}</p>
        <p className="mt-2 text-sm text-muted-foreground">{mobile && !showDesktopApps && compatible.length === 0 ? "App owners enable mobile support after checking their app on phones and tablets." : "Try a different search or category."}</p>
        {mobile && !showDesktopApps && <Button className="mt-4" variant="outline" onClick={() => setShowDesktopApps(true)}>Show desktop apps</Button>}
      </div>}
    </div>
  );
}
